import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar } from "lucide-react";
import { api } from "@/lib/api";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch { return ""; }
}

/**
 * RelatedPosts — fetches up to 3 published posts in the same category,
 * excluding the current post. Hides itself if there is nothing to show.
 */
export default function RelatedPosts({ category, currentSlug }) {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    if (!category) return;
    api
      .get(`/blog/posts?category=${encodeURIComponent(category)}&limit=10`)
      .then((r) => {
        const others = (r.data.posts || []).filter((p) => p.slug !== currentSlug).slice(0, 3);
        setPosts(others);
      })
      .catch(() => setPosts([]));
  }, [category, currentSlug]);

  if (!posts.length) return null;

  return (
    <aside className="mt-16" data-testid="related-posts">
      <div className="text-[11px] uppercase tracking-[0.2em] text-accent mb-3">Related reading</div>
      <h2 className="font-serif text-2xl sm:text-3xl text-foreground mb-6">
        More on {category}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {posts.map((p) => (
          <Link
            key={p.id}
            to={`/blog/${p.slug}`}
            className="group rounded-xl border border-border bg-card hover:border-primary/40 hover:shadow-sm transition-all overflow-hidden flex flex-col"
            data-testid={`related-card-${p.slug}`}
          >
            {p.cover_image ? (
              <div className="aspect-[16/9] w-full bg-secondary overflow-hidden">
                <img
                  src={p.cover_image}
                  alt={p.title}
                  className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                  loading="lazy"
                />
              </div>
            ) : null}
            <div className="p-4 flex-1 flex flex-col">
              <div className="text-[11px] flex items-center gap-1 text-muted-foreground mb-2">
                <Calendar className="h-3 w-3" /> {formatDate(p.created_at)}
              </div>
              <h3 className="font-serif text-base text-foreground group-hover:text-primary transition-colors line-clamp-3">
                {p.title}
              </h3>
              <div className="mt-3 inline-flex items-center gap-1 text-xs text-primary">
                Read <ArrowRight className="h-3 w-3" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </aside>
  );
}
